import { AppIcon } from '@/components/ui/app-icon';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAppSumoUpgrade } from '@/hooks/use-appsumo-upgrade.hook';
import {
  appSumoUpgradeHref,
  WHATS_NEW_CLU_ANNOUNCEMENT_ID,
  whatsNewCluCtaLabel,
} from '@/lib/appsumo-upgrade';
import {
  iconChevronLeft,
  iconChevronRight,
  iconExpand,
  iconXmark,
} from '@/lib/icons';
import { cn } from '@/lib/utils';
import { useNavigate } from '@tanstack/react-router';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useState } from 'react';
import { useWhatsNew } from './use-whats-new.hook';
import type { Announcement } from './whats-new.util';

const NAV_BUTTON =
  'grid h-6 w-6 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-black/[0.05] hover:text-foreground focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/50 disabled:pointer-events-none disabled:opacity-40 dark:hover:bg-[#262626]';

/**
 * Notion's What's new card: it slides up over the foot of the sidebar's list,
 * just above the footer, by itself while an announcement is unseen and
 * whenever the sidebar's What's new box is clicked, with a muted demo loop,
 * and opens the full video on click. The X closes it, and the announcements
 * on it count as seen. Several announcements show one at a time, stepped with
 * dots and arrows. Folded to the rail, it pops out beside the rail instead, at
 * the foot of the page, over the Clu dock if that is open (see the sidebar's
 * layer). Mounted between the tabbed sidebar's list and its footer.
 */
export function WhatsNewCard({ collapsed = false }: { collapsed?: boolean }) {
  const { announcements, replayKey, startIndex, close } = useWhatsNew();
  const posthog = usePostHog();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion() ?? false;
  const [expanded, setExpanded] = useState(false);
  // Each open starts again, at the first announcement that was unread.
  const [position, setPosition] = useState({
    replayKey,
    index: startIndex,
    direction: 1,
  });

  const count = announcements.length;
  const current = position.replayKey === replayKey;
  const index = Math.min(
    current ? position.index : startIndex,
    Math.max(0, count - 1)
  );
  const direction = current ? position.direction : 1;
  const active: Announcement | undefined = announcements[index];
  const activeId = active?.id;
  // An AppSumo-only workspace can't open Clu on its deal, so while the
  // upgrade price is open the Clu card's button goes to that offer instead.
  // The Sequences card keeps its own: its film shows LinkedIn steps.
  const appSumoOffer = useAppSumoUpgrade();
  const cta =
    appSumoOffer.active &&
    appSumoOffer.prices &&
    activeId === WHATS_NEW_CLU_ANNOUNCEMENT_ID
      ? {
          label: whatsNewCluCtaLabel(appSumoOffer.prices),
          href: appSumoUpgradeHref({ source: 'whats_new', plan: 'starter' }),
        }
      : active?.cta;

  useEffect(() => {
    if (!activeId) return;
    posthog?.capture('whats_new_shown', {
      announcement_id: activeId,
      position: index + 1,
      count,
    });
    // replayKey: each open counts as a new showing.
  }, [activeId, index, count, replayKey, posthog]);

  const capture = (event: string, extra?: Record<string, unknown>) =>
    posthog?.capture(event, {
      announcement_id: activeId,
      position: index + 1,
      count,
      ...extra,
    });

  const goTo = (next: number) =>
    setPosition({ replayKey, index: next, direction: next > index ? 1 : -1 });

  const dismiss = () => {
    capture('whats_new_dismissed');
    close();
  };

  const openCta = (cta: NonNullable<Announcement['cta']>) => {
    capture('whats_new_cta_clicked');
    if (index < count - 1) goTo(index + 1);
    else close();
    if (/^https?:\/\//.test(cta.href)) {
      window.open(cta.href, '_blank', 'noopener,noreferrer');
    } else if (cta.href.includes('?')) {
      // `to` is a path only; a query string has to go as an href.
      void navigate({ href: cta.href });
    } else {
      void navigate({ to: cta.href });
    }
  };

  const offset = reduceMotion ? 0 : 28;
  const slide = reduceMotion ? 0 : 24;

  return (
    // Folded, the card is placed against the sidebar itself (the nearest
    // positioned box), low beside the rail, level with the footer's box.
    <div className={cn('h-0 shrink-0 self-stretch', !collapsed && 'relative')}>
      <AnimatePresence>
        {active && (
          <motion.section
            key={`whats-new:${replayKey}`}
            aria-label="What's new"
            initial={{ opacity: 0, y: offset }}
            animate={{
              opacity: 1,
              y: 0,
              transition: {
                y: { duration: 0.65, ease: [0.33, 1, 0.68, 1] },
                opacity: { duration: 0.5, ease: 'easeOut' },
              },
            }}
            exit={{
              opacity: 0,
              y: offset,
              transition: { duration: 0.3, ease: 'easeIn' },
            }}
            className={cn(
              'absolute z-20 overflow-hidden rounded-xl border border-black/[0.08] bg-popover text-popover-foreground',
              'shadow-[0_16px_40px_-12px_rgba(0,0,0,0.28),0_2px_6px_rgba(0,0,0,0.06)] dark:border-white/10',
              collapsed
                ? 'bottom-2 left-[calc(100%+8px)] w-[256px]'
                : 'inset-x-0 bottom-2'
            )}
          >
            <AnimatePresence mode='wait' initial={false} custom={direction}>
              <motion.div
                key={active.id}
                custom={direction}
                variants={{
                  enter: (d: number) => ({ opacity: 0, x: d * slide }),
                  center: { opacity: 1, x: 0 },
                  exit: (d: number) => ({ opacity: 0, x: -d * slide }),
                }}
                initial='enter'
                animate='center'
                exit='exit'
                transition={{ duration: 0.16, ease: 'easeOut' }}
              >
                <button
                  type='button'
                  onClick={() => {
                    capture('whats_new_expanded');
                    setExpanded(true);
                  }}
                  aria-label={`Watch: ${active.title}`}
                  className='group relative block aspect-video w-full overflow-hidden bg-[#0c0d10] focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-inset'
                >
                  <video
                    key={active.videoUrl}
                    src={active.videoUrl}
                    poster={active.posterUrl}
                    autoPlay={!reduceMotion}
                    muted
                    // The last clip loops; the rest hand over to the next one
                    // so an opened card plays through every announcement.
                    loop={index === count - 1}
                    onEnded={() => {
                      if (index < count - 1) goTo(index + 1);
                    }}
                    playsInline
                    preload='metadata'
                    className='h-full w-full object-cover'
                  />
                  <span
                    aria-hidden
                    className={cn(
                      'absolute right-1.5 bottom-1.5 flex h-6 items-center gap-1 rounded-md bg-[rgba(0,0,0,0.55)] px-1.5 text-[10px] font-medium text-white transition-opacity',
                      reduceMotion
                        ? 'opacity-100'
                        : 'opacity-0 group-hover:opacity-100'
                    )}
                  >
                    <AppIcon icon={iconExpand} style={{ fontSize: 9 }} />
                    Watch
                  </span>
                </button>
                <div
                  className={cn('px-3 pt-2.5', count > 1 ? 'pb-1.5' : 'pb-3')}
                >
                  <span className='text-[11px] font-semibold tracking-wide text-[#1F4FD1] uppercase dark:text-primary'>
                    New
                  </span>
                  <p className='mt-0.5 text-[13px] leading-snug font-semibold'>
                    {active.title}
                  </p>
                  {active.body && (
                    <p className='mt-1 text-xs leading-relaxed text-muted-foreground'>
                      {active.body}
                    </p>
                  )}
                  {cta && (
                    <button
                      type='button'
                      onClick={() => openCta(cta)}
                      className='mt-2.5 h-7 rounded-md bg-primary px-2.5 text-xs font-medium text-primary-foreground transition-opacity hover:opacity-90 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/50'
                    >
                      {cta.label}
                    </button>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>

            {count > 1 && (
              <div className='flex items-center justify-between px-2 pb-2'>
                <div className='flex items-center'>
                  {announcements.map((a, i) => (
                    <button
                      key={a.id}
                      type='button'
                      onClick={() => goTo(i)}
                      aria-label={`Update ${i + 1} of ${count}`}
                      aria-current={i === index ? 'true' : undefined}
                      className='grid h-5 place-items-center px-1 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/50'
                    >
                      <span
                        className={cn(
                          'block h-1.5 rounded-full transition-all',
                          i === index
                            ? 'w-3.5 bg-foreground/70'
                            : 'w-1.5 bg-foreground/20 hover:bg-foreground/40'
                        )}
                      />
                    </button>
                  ))}
                </div>
                <div className='flex items-center gap-0.5'>
                  <button
                    type='button'
                    onClick={() => goTo(index - 1)}
                    disabled={index === 0}
                    aria-label='Previous update'
                    className={NAV_BUTTON}
                  >
                    <AppIcon icon={iconChevronLeft} style={{ fontSize: 10 }} />
                  </button>
                  <button
                    type='button'
                    onClick={() => goTo(index + 1)}
                    disabled={index === count - 1}
                    aria-label='Next update'
                    className={NAV_BUTTON}
                  >
                    <AppIcon icon={iconChevronRight} style={{ fontSize: 10 }} />
                  </button>
                </div>
              </div>
            )}

            <button
              type='button'
              onClick={dismiss}
              aria-label="Dismiss what's new"
              className='absolute top-1.5 right-1.5 grid h-6 w-6 place-items-center rounded-full bg-[rgba(0,0,0,0.5)] text-white transition-colors hover:bg-[rgba(0,0,0,0.72)] focus:outline-hidden focus-visible:ring-2 focus-visible:ring-white/70'
            >
              <AppIcon icon={iconXmark} style={{ fontSize: 11 }} />
            </button>
          </motion.section>
        )}
      </AnimatePresence>

      {active && (
        <Dialog open={expanded} onOpenChange={setExpanded}>
          {/* Media leads, copy follows — the dialog reads as the launch asset
              it is, not a utility modal. The close control is restyled to sit
              legibly on the video it now overlaps. */}
          <DialogContent
            className={cn(
              // Sized off the viewport in both directions. The video sits in a
              // column rather than across the top, which is what keeps this
              // short enough to fit — and lets it fill its box at 16:9 instead
              // of being letterboxed by a height clamp.
              'w-[min(56rem,calc(100vw-2rem))] max-w-none gap-0 p-0',
              'max-h-[calc(100dvh-2rem)] overflow-hidden rounded-2xl sm:rounded-2xl',
              '[&>button]:top-3 [&>button]:right-3 [&>button]:z-10 [&>button]:grid [&>button]:h-7 [&>button]:w-7',
              '[&>button]:place-items-center [&>button]:rounded-full [&>button]:bg-black/55',
              '[&>button]:text-white [&>button]:opacity-100 [&>button]:hover:bg-black/75'
            )}
          >
            <AnimatePresence mode='wait' initial={false} custom={direction}>
              <motion.div
                key={active.id}
                custom={direction}
                variants={{
                  enter: (d: number) => ({ opacity: 0, x: d * slide }),
                  center: { opacity: 1, x: 0 },
                  exit: (d: number) => ({ opacity: 0, x: -d * slide }),
                }}
                initial='enter'
                animate='center'
                exit='exit'
                transition={{ duration: 0.2, ease: 'easeOut' }}
                // Core utilities only: an arbitrary grid-template that fails to
                // compile collapses this to one column and buries the copy
                // under the video, which is exactly what went wrong before.
                className='flex flex-col sm:flex-row'
              >
                {/* Left: the video alone, 7/12 of the width at 16:9. */}
                <div className='relative aspect-video bg-black sm:w-7/12 sm:shrink-0'>
                  {/* Mounted only while open, so the full cut is never fetched
                      until asked for. Muted: Chrome blocks an autoplay that
                      makes noise, so an unmuted one wouldn't start at all. */}
                  {expanded ? (
                    <video
                      // Keyed so stepping to another update mounts a fresh
                      // element and actually restarts playback.
                      key={active.id}
                      className='absolute inset-0 h-full w-full object-cover'
                      src={active.fullVideoUrl ?? active.videoUrl}
                      poster={active.posterUrl}
                      autoPlay
                      muted
                      controls
                      playsInline
                    />
                  ) : null}
                </div>

                {/* Right: what it is, then what to do about it. */}
                <div className='flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto border-border/60 max-sm:border-t sm:border-l'>
                  <div className='shrink-0 px-4 pt-3.5 pb-3'>
                    <span className='text-[10px] font-semibold tracking-wide text-[#1F4FD1] uppercase dark:text-primary'>
                      New
                    </span>
                    <DialogTitle className='mt-0.5 text-sm leading-snug font-semibold tracking-tight'>
                      {active.title}
                    </DialogTitle>
                    {active.body && (
                      <DialogDescription className='mt-1 text-[11.5px] leading-snug'>
                        {active.body}
                      </DialogDescription>
                    )}
                  </div>

                  <div
                    className={cn(
                      'flex flex-1 flex-col px-4 pb-3.5',
                      count > 1 && 'pb-9'
                    )}
                  >
                    {active.steps?.length ? (
                      <ol className='space-y-2.5 border-t border-border/60 pt-3'>
                        {active.steps.map((step, i) => (
                          <li key={step.title} className='flex gap-2.5'>
                            <span className='mt-px grid h-4 w-4 shrink-0 place-items-center rounded-full bg-primary/10 text-[9px] font-semibold text-primary'>
                              {i + 1}
                            </span>
                            <span className='min-w-0'>
                              <span className='block text-[12px] leading-snug font-medium'>
                                {step.title}
                              </span>
                              {step.detail && (
                                <span className='mt-0.5 block text-[11px] leading-snug text-muted-foreground'>
                                  {step.detail}
                                </span>
                              )}
                            </span>
                          </li>
                        ))}
                      </ol>
                    ) : null}
                    {cta && (
                      <button
                        type='button'
                        onClick={() => {
                          setExpanded(false);
                          openCta(cta);
                        }}
                        className='mt-3 h-8 w-full shrink-0 rounded-lg bg-primary px-4 text-[12px] font-medium text-primary-foreground transition-opacity hover:opacity-90 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/50'
                      >
                        {cta.label}
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Pinned to the dialog's own bottom-right, outside the animated
                content so it stays put while the update behind it changes. */}
            {count > 1 && (
              <div className='absolute right-3 bottom-2.5 z-10 flex items-center gap-1.5'>
                <div className='flex items-center'>
                  {announcements.map((a, i) => (
                    <button
                      key={a.id}
                      type='button'
                      onClick={() => goTo(i)}
                      aria-label={`Update ${i + 1} of ${count}`}
                      aria-current={i === index ? 'true' : undefined}
                      className='grid h-5 place-items-center px-1 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/50'
                    >
                      <span
                        className={cn(
                          'block h-1.5 rounded-full transition-all',
                          i === index
                            ? 'w-3.5 bg-foreground/70'
                            : 'w-1.5 bg-foreground/20 hover:bg-foreground/40'
                        )}
                      />
                    </button>
                  ))}
                </div>
                <button
                  type='button'
                  onClick={() => goTo(index - 1)}
                  disabled={index === 0}
                  aria-label='Previous update'
                  className={NAV_BUTTON}
                >
                  <AppIcon icon={iconChevronLeft} style={{ fontSize: 10 }} />
                </button>
                <button
                  type='button'
                  onClick={() => goTo(index + 1)}
                  disabled={index === count - 1}
                  aria-label='Next update'
                  className={NAV_BUTTON}
                >
                  <AppIcon icon={iconChevronRight} style={{ fontSize: 10 }} />
                </button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
